/**
 * DocumentOutputSection Component
 *
 * The output settings of one document type: its terms and footer note, in each output language.
 * A blank language shows the other language's text, which is what the PDF prints instead.
 */
import { kebabCase } from 'es-toolkit';
import { Button, Loader, Paper } from '@insio/ui-toolkit';
import type { OutputDocumentType } from '@insio/shared-types';
import { useDelayedLoading } from '@/hooks/use-delayed-loading';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { LocalizedTextField } from './LocalizedTextField';
import {
  OUTPUT_TEXT_FIELDS,
  OUTPUT_TEXT_MAX_LENGTH,
  OUTPUT_TEXT_ROWS,
  useDocumentOutputSection
} from './use-document-output-section';

interface DocumentOutputSectionProps {
  readonly documentType: OutputDocumentType;
  readonly testId: string;
}

export function DocumentOutputSection({ documentType, testId }: DocumentOutputSectionProps) {
  const t = useFeatureTranslation('systemSettings.documentOutput');
  const { showSuccess, showError } = useToastHelpers();
  const { settings, loading, error, values, isTooLong, canSave, saving, setText, save } =
    useDocumentOutputSection(documentType);
  const showLoader = useDelayedLoading(loading && !settings);

  const handleSave = async () => {
    if (await save()) {
      showSuccess(t('saveSuccess'));
    } else {
      showError(t('saveError'));
    }
  };

  return (
    <Paper heading='stripe' bleed padding='none' testId={testId}>
      <Paper.Title>
        <h2>{t(`sections.${documentType}.title`)}</h2>
      </Paper.Title>
      <Paper.Content className='space-y-md'>
        <p className='text-sm text-foreground-muted'>{t(`sections.${documentType}.description`)}</p>
        {error ? (
          <p className='text-sm text-error-text'>{t('loadError')}</p>
        ) : settings ? (
          <div className='space-y-lg'>
            {OUTPUT_TEXT_FIELDS.map(field => (
              <LocalizedTextField
                key={field}
                label={t(`${field}.label`)}
                hint={t(`${field}.hint`)}
                value={values[field]}
                placeholder={locale => t(`${field}.placeholder`, { lng: locale })}
                maxLength={OUTPUT_TEXT_MAX_LENGTH[field]}
                rows={OUTPUT_TEXT_ROWS[field]}
                isTooLong={locale => isTooLong(field, locale)}
                // Locked while a save is in flight, so nothing typed meanwhile is dropped with it.
                disabled={saving}
                onChange={(locale, text) => setText(field, locale, text)}
                testId={`${testId}-${kebabCase(field)}`}
              />
            ))}
            <Button
              variant='primary'
              onClick={handleSave}
              disabled={!canSave}
              testId={`${testId}-save`}
            >
              {saving ? t('saving') : t('save')}
            </Button>
          </div>
        ) : (
          showLoader && <Loader.Section />
        )}
      </Paper.Content>
    </Paper>
  );
}
